import React, {useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Row, Spinner, Table} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import {AtomProdottiAPI} from "api";

const moment = require("moment");

/**
 * I PRODOTTI DI ATOM: COSA APRONO QUI (4/11/2026, «ATOM · VENDITE» task 1.3).
 *
 * ┌─ IL GUASTO ──────────────────────────────────────────────────────────────────────────────┐
 * │ Un prodotto nuovo su ATOM (gli Omaggi 307–322, la Promo 323 del 29–30/10) arrivava qui    │
 * │ senza corrispondenza: il ponte fermava la vendita («PRODOTTO_NON_MAPPATO») e il cliente   │
 * │ non entrava in lista d'attesa. La mappa si scriveva solo a mano sul database.             │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Qui l'amministratore:
 *   · vede le vendite ferme e quale prodotto manca;
 *   · dice a cosa corrisponde un prodotto di ATOM: una classe (livello), un videocorso, un
 *     piano di videocorsi, un abbonamento alle live — e, per i pacchetti, piu' livelli;
 *   · rilancia le vendite ferme, che il ponte riprova entro un minuto.
 * Il server controlla tutto (rotte `/integrations/atom/products` e `/integrations/atom/inbox`).
 */
const TIPI = [
    {valore: "LEVEL", etichetta: "Classe (livello)"},
    {valore: "COURSE", etichetta: "Videocorso"},
    {valore: "PLAN_COURSES", etichetta: "Piano di videocorsi"},
    {valore: "LIVE", etichetta: "Abbonamento alle lezioni live"}
];
const vuota = {external_product_id: "", external_product_name: "", target_type: "LEVEL", id_level: "", id_course: "",
    individual: false, hours: "", plan_level_ids: [], plan_course_ids: [], live_quota_per_period: "", live_duration_days: ""};

/** Il prodotto che manca, letto dal motivo della consegna ferma («nessuna corrispondenza per: 323, 324»). */
function prodottiMancanti(consegna) {
    const m = String(consegna.last_error || "").match(/nessuna corrispondenza per:\s*([0-9,\s]+)/i);
    return m ? m[1].split(",").map((x) => x.trim()).filter(Boolean) : [];
}

const AtomProdotti = () => {
    const [carico, setCarico] = useState(true);
    const [dati, setDati] = useState({map: [], courses: [], levels: []});
    const [fermi, setFermi] = useState([]);
    const [riga, setRiga] = useState(vuota);
    const [errore, setErrore] = useState(null);
    const [salvo, setSalvo] = useState(false);

    const ricarica = () => {
        setCarico(true);
        Promise.all([AtomProdottiAPI.mappa(), AtomProdottiAPI.fermi()]).then(([m, f]) => {
            setDati({map: m.map || [], courses: m.courses || [], levels: m.levels || []});
            setFermi((f.deliveries || []).filter((d) => d.blocked_reason === "PRODOTTO_NON_MAPPATO"));
        }).catch((e) => setErrore((e && (e.message || e.status)) || "Non è stato possibile leggere i dati"))
            .finally(() => setCarico(false));
    };
    useEffect(ricarica, []);

    const nomeLivello = useMemo(() => Object.fromEntries(dati.levels.map((l) => [l.id, l.name])), [dati.levels]);
    const nomeCorso = useMemo(() => Object.fromEntries(dati.courses.map((c) => [c.id, c.name])), [dati.courses]);
    const mappati = useMemo(() => new Set(dati.map.map((r) => String(r.external_product_id))), [dati.map]);
    const mancanti = useMemo(() => [...new Set(fermi.flatMap(prodottiMancanti))].filter((p) => !mappati.has(p)), [fermi, mappati]);

    const alterna = (campo, id) => setRiga((r) => Object.assign({}, r, {[campo]: r[campo].includes(id) ? r[campo].filter((x) => x !== id) : r[campo].concat([id])}));

    const salva = () => {
        setErrore(null);
        setSalvo(true);
        const corpo = {
            external_product_id: String(riga.external_product_id).trim(),
            external_product_name: riga.external_product_name || null,
            target_type: riga.target_type,
            individual: riga.target_type === "LEVEL" ? !!riga.individual : false,
            hours: riga.target_type === "LEVEL" && riga.individual && riga.hours ? Number(riga.hours) : null
        };
        if (riga.target_type === "LEVEL") { corpo.id_level = Number(riga.id_level); corpo.plan_level_ids = riga.plan_level_ids; }
        if (riga.target_type === "COURSE") corpo.id_course = Number(riga.id_course);
        if (riga.target_type === "PLAN_COURSES" || riga.target_type === "LIVE") corpo.plan_course_ids = riga.plan_course_ids;
        if (riga.target_type === "LIVE") { corpo.live_quota_per_period = Number(riga.live_quota_per_period); corpo.live_duration_days = Number(riga.live_duration_days); }
        AtomProdottiAPI.salva(corpo).then(() => {
            setRiga(vuota);
            ricarica();
            global.SweetAlert.fire({icon: "success", title: "Corrispondenza salvata", text: "Ora puoi rilanciare le vendite ferme di questo prodotto."});
        }).catch((e) => setErrore((e && (e.message || e.status)) || "Non salvata")).finally(() => setSalvo(false));
    };

    const modifica = (r) => setRiga(Object.assign({}, vuota, r, {
        id_level: r.id_level || "", id_course: r.id_course || "", hours: r.hours || "",
        live_quota_per_period: r.live_quota_per_period || "", live_duration_days: r.live_duration_days || "",
        plan_level_ids: r.plan_level_ids || [], plan_course_ids: r.plan_course_ids || []
    }));

    const togli = (r) => global.SweetAlert.fire({
        icon: "warning", title: "Togliere la corrispondenza del prodotto " + r.external_product_id + "?",
        text: "Le prossime vendite di questo prodotto si fermeranno finché non la rimetti.",
        showCancelButton: true, confirmButtonText: "Togli", cancelButtonText: "Annulla"
    }).then((s) => { if (s.value) AtomProdottiAPI.togli(r.id).then(ricarica).catch((e) => setErrore(e && (e.message || e.status))); });

    const rilancia = (d) => AtomProdottiAPI.rilancia(d.id).then(() => {
        global.SweetAlert.fire({icon: "success", title: "Rimessa in coda", text: "Il ponte la riprova entro un minuto.", timer: 2500, showConfirmButton: false});
        ricarica();
    }).catch((e) => setErrore(e && (e.message || e.status)));

    const descrivi = (r) => {
        if (r.target_type === "LEVEL") {
            const altri = (r.plan_level_ids || []).map((id) => nomeLivello[id] || id);
            return (nomeLivello[r.id_level] || ("livello " + r.id_level)) + (r.individual ? " (individuale, " + (r.hours || "?") + " h)" : "")
                + (altri.length ? " · pacchetto: " + altri.join(" + ") : "");
        }
        if (r.target_type === "COURSE") return "Videocorso: " + (nomeCorso[r.id_course] || r.id_course);
        const corsi = (r.plan_course_ids || []).map((id) => nomeCorso[id] || id).join(", ");
        if (r.target_type === "LIVE") return "Live " + r.live_quota_per_period + " per ciclo, " + r.live_duration_days + " giorni" + (corsi ? " · videocorsi: " + corsi : "");
        return "Piano: " + (corsi || "nessun videocorso");
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Prodotti ATOM" pageTitle="Amministrazione"/>
                {errore ? <Alert color="danger">{errore}</Alert> : null}
                {carico ? <div className="text-center my-4"><Spinner color="primary"/></div> : null}

                <Card>
                    <CardHeader><h5 className="card-title mb-0">Vendite ferme per un prodotto senza corrispondenza</h5></CardHeader>
                    <CardBody>
                        {mancanti.length ? (
                            <Alert color="warning">
                                Prodotti di ATOM da collegare: <b>{mancanti.join(", ")}</b>. Collegali qui sotto, poi premi «Rilancia» sulle vendite.
                            </Alert>
                        ) : null}
                        {fermi.length === 0 ? <p className="text-muted mb-0">Nessuna vendita ferma per questo motivo.</p> : (
                            <Table responsive className="align-middle mb-0">
                                <thead><tr><th>#</th><th>Arrivata</th><th>Evento</th><th>Cliente ATOM</th><th>Motivo</th><th></th></tr></thead>
                                <tbody>
                                {fermi.map((d) => (
                                    <tr key={d.id}>
                                        <td>{d.id}</td>
                                        <td>{moment(d.createdAt).format("DD/MM/YYYY HH:mm")}</td>
                                        <td>{d.event}</td>
                                        <td>{d.client_external_id || "—"}</td>
                                        <td className="text-muted small">{d.last_error}</td>
                                        <td className="text-end">
                                            <Button size="sm" color="primary" disabled={prodottiMancanti(d).some((p) => !mappati.has(p))}
                                                    title={prodottiMancanti(d).some((p) => !mappati.has(p)) ? "Prima collega il prodotto" : ""}
                                                    onClick={() => rilancia(d)}>Rilancia</Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader><h5 className="card-title mb-0">{riga.id ? "Modifica la corrispondenza" : "Collega un prodotto di ATOM"}</h5></CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={3}><FormGroup><Label>Numero del prodotto su ATOM</Label>
                                <Input value={riga.external_product_id} disabled={!!riga.id} placeholder="es. 323"
                                       onChange={(e) => setRiga(Object.assign({}, riga, {external_product_id: e.target.value.replace(/[^0-9]/g, "")}))}/></FormGroup></Col>
                            <Col md={5}><FormGroup><Label>Nome (solo per riconoscerlo)</Label>
                                <Input value={riga.external_product_name || ""} onChange={(e) => setRiga(Object.assign({}, riga, {external_product_name: e.target.value}))}/></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Cosa apre</Label>
                                <Input type="select" value={riga.target_type} onChange={(e) => setRiga(Object.assign({}, riga, {target_type: e.target.value}))}>
                                    {TIPI.map((t) => <option key={t.valore} value={t.valore}>{t.etichetta}</option>)}
                                </Input></FormGroup></Col>
                        </Row>

                        {riga.target_type === "LEVEL" ? (
                            <Row>
                                <Col md={4}><FormGroup><Label>Livello</Label>
                                    <Input type="select" value={riga.id_level} onChange={(e) => setRiga(Object.assign({}, riga, {id_level: e.target.value}))}>
                                        <option value="">Scegli…</option>
                                        {dati.levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </Input></FormGroup></Col>
                                <Col md={4}><FormGroup check className="mt-4">
                                    <Input type="checkbox" id="atom-individuale" checked={!!riga.individual} onChange={(e) => setRiga(Object.assign({}, riga, {individual: e.target.checked}))}/>
                                    <Label check for="atom-individuale">Lezioni individuali</Label></FormGroup>
                                    {riga.individual ? <Input className="mt-2" type="number" min={1} placeholder="Ore" value={riga.hours}
                                                              onChange={(e) => setRiga(Object.assign({}, riga, {hours: e.target.value}))}/> : null}
                                </Col>
                                <Col md={12}><Label>Pacchetto: più livelli (es. A1 + A2). Se ne scegli, la vendita mette il cliente in lista d'attesa di ognuno.</Label>
                                    <div className="d-flex flex-wrap gap-3">
                                        {dati.levels.map((l) => (
                                            <FormGroup check key={l.id}>
                                                <Input type="checkbox" id={"pl-" + l.id} checked={riga.plan_level_ids.includes(l.id)} onChange={() => alterna("plan_level_ids", l.id)}/>
                                                <Label check for={"pl-" + l.id}>{l.name}</Label>
                                            </FormGroup>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        ) : null}

                        {riga.target_type === "COURSE" ? (
                            <FormGroup><Label>Videocorso</Label>
                                <Input type="select" value={riga.id_course} onChange={(e) => setRiga(Object.assign({}, riga, {id_course: e.target.value}))}>
                                    <option value="">Scegli…</option>
                                    {dati.courses.map((c) => <option key={c.id} value={c.id}>{c.name} (#{c.id})</option>)}
                                </Input></FormGroup>
                        ) : null}

                        {riga.target_type === "LIVE" ? (
                            <Row>
                                <Col md={3}><FormGroup><Label>Lezioni per ciclo</Label>
                                    <Input type="number" min={1} value={riga.live_quota_per_period} onChange={(e) => setRiga(Object.assign({}, riga, {live_quota_per_period: e.target.value}))}/></FormGroup></Col>
                                <Col md={3}><FormGroup><Label>Durata (giorni)</Label>
                                    <Input type="number" min={1} value={riga.live_duration_days} onChange={(e) => setRiga(Object.assign({}, riga, {live_duration_days: e.target.value}))}/></FormGroup></Col>
                            </Row>
                        ) : null}

                        {(riga.target_type === "PLAN_COURSES" || riga.target_type === "LIVE") ? (
                            <FormGroup><Label>Videocorsi che apre</Label>
                                <div className="d-flex flex-wrap gap-3">
                                    {dati.courses.map((c) => (
                                        <FormGroup check key={c.id}>
                                            <Input type="checkbox" id={"pc-" + c.id} checked={riga.plan_course_ids.includes(c.id)} onChange={() => alterna("plan_course_ids", c.id)}/>
                                            <Label check for={"pc-" + c.id}>{c.name} <span className="text-muted">#{c.id}</span></Label>
                                        </FormGroup>
                                    ))}
                                </div>
                            </FormGroup>
                        ) : null}

                        <div className="text-end">
                            {riga.id ? <Button color="light" className="me-2" onClick={() => setRiga(vuota)}>Annulla</Button> : null}
                            <Button color="primary" disabled={salvo || !riga.external_product_id} onClick={salva}>
                                {salvo ? <Spinner size="sm" className="me-1"/> : null}Salva
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader><h5 className="card-title mb-0">Prodotti collegati <Badge color="secondary">{dati.map.length}</Badge></h5></CardHeader>
                    <CardBody>
                        <Table responsive className="align-middle mb-0">
                            <thead><tr><th>ATOM</th><th>Nome</th><th>Apre</th><th></th></tr></thead>
                            <tbody>
                            {dati.map.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.external_product_id}</td>
                                    <td>{r.external_product_name}</td>
                                    <td className="small">{descrivi(r)}</td>
                                    <td className="text-end text-nowrap">
                                        <Button size="sm" color="light" className="me-1" onClick={() => modifica(r)}>Modifica</Button>
                                        <Button size="sm" color="danger" outline onClick={() => togli(r)}>Togli</Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default AtomProdotti;
